import { useCustom } from "@refinedev/core";
import { AlertTriangle, Search, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router";

import { Combo } from "@/components/Combo";
import { query } from "@/lib/api";
import {
  barcodeWord,
  categoryShort,
  errorDisplay,
  errorMessage,
  productWord,
  sizeWord,
} from "@/lib/format";
import type { ProductFilters, Products } from "@/lib/providers";
import { useSessionGuard } from "@/lib/useSessionGuard";

const FIELD =
  "h-8 rounded-[var(--radius-ui)] border border-border bg-bg px-2 text-[13px] focus:border-border-strong";

/** Сколько строк добавляет одно нажатие «показать ещё». Ровно страница API. */
const PAGE = 300;

/**
 * Список товаров.
 *
 * Товар здесь — МОДЕЛЬ, а не размер: у одной вещи столько карточек, сколько у
 * неё размеров (у 14623 их одиннадцать), и список из одиннадцати одинаковых
 * строк «Платье вечернее» — не список.
 *
 * Список — это КАТАЛОГ МАРКЕТПЛЕЙСА, а не содержимое коробов (решение
 * владельца 07.09.26): 1 771 заведённый товар против 683 лежащих на складе.
 * Товар с нулём стоит в нём наравне с прочими — «закончилось» и «такого у нас
 * нет» разные ответы, и до этого дня экран их не различал.
 *
 * Все пять фильтров живут в АДРЕСЕ. Это не то же самое, что фильтр на экране
 * размещения: там линза поверх посчитанного отчёта, здесь — сам запрос, и
 * ссылку на найденный товар отправляют сменщику.
 *
 * Фильтры трёх РАЗНЫХ пород, и оформлены они поэтому по-разному. Модель и
 * баркод ищут ВЕЩЬ: их набирают руками (баркод — сканером, одной строкой с
 * Enter'ом), и уезжают они по отправке формы. Поставщик и категория сужают
 * КАТАЛОГ, город — СКЛАД: их выбирают из готового справочника, и там ждать
 * кнопки незачем — выбор применяется сразу.
 */
export function ProductsPage() {
  const [params, setParams] = useSearchParams();
  const modelParam = params.get("model") ?? "";
  const barcodeParam = params.get("barcode") ?? "";
  const supplierParam = params.get("supplier") ?? "";
  const categoryParam = params.get("cat") ?? "";
  const cityParam = params.get("city") ?? "";
  // Сколько строк просить. Живёт в адресе вместе с фильтрами: нажатое
  // «показать ещё» обязано пережить перезагрузку страницы, иначе ссылка,
  // отправленная сменщику, откроется у него обрезанной.
  const limitParam = params.get("n") ?? "";

  // Поля живут в состоянии, а запрос — в адресе. Иначе каждая набранная
  // цифра уходила бы в CRM отдельным запросом, а история браузера
  // заполнялась бы по записи на символ.
  const [model, setModel] = useState(modelParam);
  const [barcode, setBarcode] = useState(barcodeParam);
  useEffect(() => setModel(modelParam), [modelParam]);
  useEffect(() => setBarcode(barcodeParam), [barcodeParam]);

  /** Правит ОДИН ключ адреса, не трогая остальные. */
  const set = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    // Смена ОТБОРА возвращает список к первой странице: поднятый до девятисот
    // потолок относился к прежнему отбору, и тащить его в новый значит
    // привезти девятьсот строк там, где человек хотел посмотреть первые.
    if (key !== "n") next.delete("n");
    setParams(next, { replace: true });
  };

  const submit = (next: { model: string; barcode: string }) => {
    // Отбор поставщиком и категорией переживает отправку формы: он выбран
    // раньше и человек его не отменял. Собранный с нуля адрес молча стирал бы
    // выбор, сделанный соседним полем.
    const search = new URLSearchParams(params);
    const put = (key: string, value: string) => {
      if (value.trim()) search.set(key, value.trim());
      else search.delete(key);
    };
    put("model", next.model);
    put("barcode", next.barcode);
    setParams(search, { replace: true });
  };

  const reset = () => {
    setModel("");
    setBarcode("");
    setParams(new URLSearchParams(), { replace: true });
  };

  const url = useMemo(
    () =>
      `/api/products${query({
        model_id: modelParam || undefined,
        barcode: barcodeParam || undefined,
        supplier_id: supplierParam || undefined,
        category_id: categoryParam || undefined,
        city_id: cityParam || undefined,
        limit: limitParam || undefined,
      })}`,
    [modelParam, barcodeParam, supplierParam, categoryParam, cityParam, limitParam],
  );

  const listQuery = useCustom<Products>({ url, method: "get" });
  // Справочники отбора — отдельным запросом и один раз: собранные по показанной
  // странице, они врали бы её потолком. Отказ здесь не ломает экран: списки
  // фильтров останутся пустыми, а список товаров работает своим запросом.
  const dictQuery = useCustom<ProductFilters>({
    url: "/api/products/filters",
    method: "get",
  });
  const suppliers = dictQuery.result.data?.suppliers ?? [];
  const categories = dictQuery.result.data?.categories ?? [];
  const cityOptions = dictQuery.result.data?.cities ?? [];
  // `result.data` у Refine существует и ДО загрузки — пустым объектом.
  // Списком считается только payload, в котором есть массив: проверка на
  // «не null» пропустила бы пустышку дальше и экран падал бы на `.map`.
  const loaded = listQuery.result.data;
  const list = loaded && Array.isArray(loaded.products) ? loaded : null;
  const busy = listQuery.query.isFetching;
  const error = listQuery.query.error
    ? errorMessage(listQuery.query.error)
    : "";
  useSessionGuard(listQuery.query.error);

  // Точный поиск и групповой отбор — разные обещания, и говорить о них надо
  // разное: первый ищет вещь, второй сужает список.
  const exact = !!modelParam || !!barcodeParam;
  const filtered = exact || !!supplierParam || !!categoryParam || !!cityParam;
  // Имена городов приезжают ОДИН раз — в сводке, а строки несут только номера.
  // Отсюда и разбор: строке нужно имя, а возить его в каждой из 1 771 строки
  // значило бы возить одно и то же слово тысячу семьсот раз.
  const cityName = useMemo(() => {
    const names = new Map<number, string>();
    for (const c of list?.cities ?? []) names.set(c.city_id, c.name);
    for (const c of cityOptions) if (!names.has(c.id)) names.set(c.id, c.name);
    return names;
  }, [list, cityOptions]);
  // Колонка городов при выбранном городе не нужна: он один и назван в шапке,
  // а колонка повторяла бы его в каждой строке.
  const showCities = !cityParam;

  if (error && errorDisplay(list !== null) === "replace") {
    return (
      <main
        className="grid flex-1 place-items-center p-5"
        data-component="ProductsPage"
      >
        <div
          role="alert"
          className="flex max-w-[420px] flex-col items-center gap-3 rounded-[var(--radius-card)] border border-err/60 bg-err-bg p-6 text-center"
        >
          <AlertTriangle size={20} className="text-err" />
          <p className="text-[13.5px]">{error}</p>
          <button
            type="button"
            onClick={() => listQuery.query.refetch()}
            className="h-8 rounded-[var(--radius-ui)] bg-fg px-3 text-[13px] font-medium text-bg"
          >
            Повторить
          </button>
        </div>
      </main>
    );
  }

  return (
    <main
      className="flex min-h-0 flex-1 flex-col overflow-y-auto p-4 md:overflow-hidden md:p-5"
      data-component="ProductsPage"
      data-annot="товары"
    >
      <div className="mb-4 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-[22px] font-semibold tracking-[-0.02em]">
            Товары{" "}
            <span className="font-normal text-fg-muted">
              {cityParam
                ? `в городе ${cityName.get(Number(cityParam)) ?? cityParam}`
                : filtered
                  ? "по фильтру"
                  : "на маркетплейсе"}
            </span>
          </h1>
          <p className="mt-1 text-[12.5px] text-fg-muted">
            {/* Что именно показано — сказано прямо, и для каждой породы
                фильтра своими словами: иначе отсутствие товара в списке
                читалось бы как «его нет в каталоге», хотя на деле его просто
                нет в коробах. Поиск и отбор поставщиком идут по КАТАЛОГУ,
                отбор городом — по СКЛАДУ. */}
            {exact
              ? "Поиск идёт по всему каталогу — товар найдётся и без остатка."
              : cityParam
                ? "Показано то, что лежит в коробах этого города."
                : filtered
                  ? "Отбор идёт по каталогу — товар в списке есть и без остатка."
                  : "Показаны все товары, заведённые у нас на маркетплейсе."}
          </p>
        </div>

        <form
          data-annot="фильтр"
          className="flex flex-wrap items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            submit({ model, barcode });
          }}
        >
          {/* Город стоит ПЕРВЫМ: он отвечает на вопрос «что у нас в Самаре»,
              с которого смена и начинается, и в отличие от двух соседей
              сужает не каталог, а склад. Счётчик поэтому обещает другое —
              «Воронеж · 283» это товары, которые там СЕЙЧАС лежат. */}
          <Combo
            label="город"
            value={cityParam}
            onChange={(v) => set("city", v)}
            width={190}
            options={[
              { value: "", label: "все" },
              ...cityOptions.map((c) => ({
                value: String(c.id),
                label: c.name,
                meta: String(c.models),
                title: `${c.name}: ${c.models} товаров в коробах`,
              })),
            ]}
          />
          {/* Счётчик рядом с именем — не украшение: у поставщика с двумя
              товарами и у поставщика со ста семьюдесятью девятью разная цена
              ошибки отбора. Считаны они по каталогу, поэтому число —
              обещание: столько строк список и покажет. */}
          <Combo
            label="поставщик"
            value={supplierParam}
            onChange={(v) => set("supplier", v)}
            options={[
              { value: "", label: "все" },
              ...suppliers.map((s) => ({
                value: String(s.id),
                label: s.name,
                meta: String(s.models),
                title: s.code ? `${s.name} · код ${s.code}` : s.name,
              })),
            ]}
          />
          <Combo
            label="категория"
            value={categoryParam}
            onChange={(v) => set("cat", v)}
            width={230}
            options={[
              { value: "", label: "все" },
              ...categories.map((c) => ({
                // В списке — полный путь («Аксессуары / Украшения на голову /
                // Фата»): две категории различаются как раз хвостом, и
                // обрезанные до последнего слова они стали бы неразличимы.
                value: String(c.id),
                label: c.name,
                meta: String(c.models),
                title: c.name,
              })),
            ]}
          />
          <span className="flex items-center gap-1.5">
            <label className="text-[12.5px] text-fg-muted" htmlFor="f-model">
              Модель
            </label>
            <input
              id="f-model"
              type="search"
              inputMode="numeric"
              autoComplete="off"
              placeholder="14623"
              className={FIELD + " w-[110px] tnum"}
              value={model}
              onChange={(e) => setModel(e.target.value)}
            />
          </span>
          <span className="flex items-center gap-1.5">
            <label className="text-[12.5px] text-fg-muted" htmlFor="f-barcode">
              Баркод
            </label>
            {/* Шире модели: баркод тринадцатизначный, и обрезанный в поле он
                не читается глазом при сверке с ярлыком. */}
            <input
              id="f-barcode"
              type="search"
              inputMode="numeric"
              autoComplete="off"
              placeholder="2042803612378"
              className={FIELD + " w-[170px] tnum"}
              value={barcode}
              onChange={(e) => setBarcode(e.target.value)}
            />
          </span>
          <button
            type="submit"
            disabled={busy}
            className="flex h-8 items-center gap-1.5 rounded-[var(--radius-ui)] bg-fg px-3 text-[13px] font-medium text-bg disabled:opacity-60"
          >
            <Search size={13} />
            {busy ? "Ищу…" : "Найти"}
          </button>
          {filtered ? (
            <button
              type="button"
              onClick={reset}
              className="flex h-8 items-center gap-1 rounded-[var(--radius-ui)] border border-border px-2.5 text-[12.5px] text-fg-muted hover:bg-bg-hover hover:text-fg"
            >
              <X size={13} />
              Сбросить
            </button>
          ) : null}
        </form>
      </div>

      {error ? (
        <div
          role="alert"
          className="mb-3 flex items-center gap-2 rounded-[var(--radius-ui)] border border-err/60 bg-err-bg px-3 py-2 text-[12.5px]"
        >
          <AlertTriangle size={14} className="text-err" />
          <span>{error}</span>
          <button
            type="button"
            onClick={() => listQuery.query.refetch()}
            className="ml-auto rounded-[var(--radius-ui)] border border-border px-2 py-0.5 text-[12px] hover:bg-bg-hover"
          >
            Повторить
          </button>
        </div>
      ) : null}

      {!list ? (
        <p className="text-[13px] text-fg-muted">Ищу…</p>
      ) : (
        <>
          <dl
            data-annot="сводка"
            // Две плитки, поэтому две колонки и на широком экране: разложенные
            // по четырём, они оставили бы половину сводки пустой.
            className="mb-4 grid grid-cols-2 gap-2"
            aria-busy={busy}
          >
            <Metric
              label="Товаров"
              value={list.total}
              unit={productWord(list.total)}
              // «Показано 300 из 1 771» здесь НЕ стоит, хотя и просится: та же
              // строка уже написана под кнопкой «показать ещё», а место в
              // плитке одно. Из двух фактов о списке важнее тот, которого
              // больше нигде нет: список — каталог, и «1 771 товар» без числа
              // остатка читалось бы как «столько у нас лежит на складе».
              note={
                cityParam
                  ? "все лежат в этом городе"
                  : `${list.in_stock} с остатком, ${list.total - list.in_stock} без`
              }
            />
            {/* Одно число на весь остаток: короба считаются единым целым — и
                упакованные, и те, что ещё набирают. Не в счёт только
                отключённые короба и уехавшие в поставку, и отсекает их канон
                на стороне сервиса, а не подпись на экране.

                А вот по городам оно разложено: одно число на всю страну не
                говорит, куда ехать за вещью, а склад у нас в трёх городах и
                весьма неравных. Разбивка считана тем же запросом, поэтому
                сумма чипов равна числу над ними — всегда, а не обычно. */}
            <Metric
              label="В коробах"
              value={list.units}
              unit="шт"
              accent
              note={list.cities.length === 0 ? "ни одной штуки" : ""}
              parts={list.cities.map((c) => ({
                key: c.city_id,
                name: c.name,
                units: c.units,
                active: cityParam === String(c.city_id),
                onClick: () => set("city", String(c.city_id)),
              }))}
            />
          </dl>

          {list.products.length === 0 ? (
            <Empty>
              {cityParam
                ? "В коробах этого города сейчас ничего не лежит."
                : filtered
                  ? "Под фильтр не подошёл ни один товар."
                  : "На маркетплейсе не заведено ни одного товара."}
              {filtered ? (
                <button
                  type="button"
                  onClick={reset}
                  className="mt-3 block h-8 rounded-[var(--radius-ui)] bg-fg px-3 text-[13px] font-medium text-bg"
                >
                  Сбросить фильтр
                </button>
              ) : null}
            </Empty>
          ) : (
            <div className="min-h-0 flex-1 overflow-auto rounded-[var(--radius-card)] border border-border bg-bg-subtle">
              <table data-annot="таблица" className="w-full border-collapse text-[13px]">
                <caption className="sr-only">
                  Товары и их остаток в коробах, по убыванию остатка
                </caption>
                <thead className="hidden md:table-header-group">
                  <tr>
                    {[
                      "Фото",
                      "Товар",
                      "Модель",
                      "Размеры",
                      ...(showCities ? ["В городах"] : []),
                      "Штук",
                      "Коробов",
                    ].map(
                      (title) => (
                        <th
                          key={title}
                          scope="col"
                          className={
                            "sticky top-0 z-10 whitespace-nowrap border-b border-border bg-bg-elevated px-2.5 py-2 text-[11px] font-medium uppercase tracking-[0.04em] text-fg " +
                            (title === "Штук" || title === "Коробов"
                              ? "text-right"
                              : "text-left")
                          }
                        >
                          {title}
                        </th>
                      ),
                    )}
                  </tr>
                </thead>
                <tbody className="block md:table-row-group">
                  {list.products.map((row) => (
                    <tr
                      key={row.model_id}
                      data-product={row.model_id}
                      data-annot={`строка ${row.model_id}`}
                      className="mb-2 block rounded-[var(--radius-ui)] border border-border bg-bg p-2 md:mb-0 md:table-row md:rounded-none md:border-0 md:border-b md:border-border md:bg-transparent md:p-0 md:hover:bg-bg-hover"
                    >
                      <Cell label="Фото">
                        {row.image_url ? (
                          /* Ссылка на CDN маркетплейса напрямую: файл грузит
                             браузер, сервис картинки не проксирует и не
                             хранит. `referrerpolicy` — чтобы наружу не уезжал
                             адрес внутренней страницы. */
                          <img
                            src={row.image_url}
                            alt=""
                            loading="lazy"
                            referrerPolicy="no-referrer"
                            className="h-11 w-8 rounded-[3px] border border-border object-cover"
                            onError={(e) => {
                              e.currentTarget.style.visibility = "hidden";
                            }}
                          />
                        ) : null}
                      </Cell>
                      <Cell label="Товар">
                        <span className="flex flex-col">
                          {/* Вся строка ведёт в карточку одной ссылкой на
                              названии: кликабельная строка целиком не даёт
                              скопировать номер и недоступна с клавиатуры. */}
                          <Link
                            to={`/products/${row.model_id}`}
                            className="font-medium text-fg hover:text-accent-text"
                          >
                            {row.name || `Товар ${row.model_id}`}
                          </Link>
                          <span className="text-[11px] tnum text-fg-muted">
                            арт. {row.article || "—"} ·{" "}
                            {row.cards} {barcodeWord(row.cards)}
                          </span>
                          {/* Поставщик и категория — кнопки, а не подпись:
                              увидев товар, спрашивают «а что ещё у этого
                              поставщика», и отбор ставится оттуда же, где
                              вопрос возник. Ссылкой они быть не могут — отбор
                              живёт в адресе того же экрана. */}
                          <span className="mt-0.5 flex flex-wrap items-center gap-1">
                            {row.supplier.id ? (
                              <Tag
                                active={supplierParam === String(row.supplier.id)}
                                title={`Только товары поставщика ${row.supplier.name}`}
                                onClick={() =>
                                  set("supplier", String(row.supplier.id))
                                }
                              >
                                {row.supplier.name}
                              </Tag>
                            ) : null}
                            {row.category.id ? (
                              <Tag
                                active={categoryParam === String(row.category.id)}
                                title={row.category.name}
                                onClick={() => set("cat", String(row.category.id))}
                              >
                                {categoryShort(row.category.name)}
                              </Tag>
                            ) : null}
                          </span>
                        </span>
                      </Cell>
                      <Cell label="Модель">
                        <span className="tnum">{row.model_id}</span>
                      </Cell>
                      <Cell label="Размеры">
                        <Sizes sizes={row.sizes} />
                      </Cell>
                      {showCities ? (
                        <Cell label="В городах">
                          <Cities
                            by={row.by_city}
                            name={cityName}
                            onPick={(id) => set("city", String(id))}
                          />
                        </Cell>
                      ) : null}
                      <Cell label="Штук" align="text-right tnum">
                        <span
                          className={
                            row.units > 0 ? "font-medium" : "text-fg-muted"
                          }
                        >
                          {row.units}
                        </span>
                      </Cell>
                      <Cell label="Коробов" align="text-right tnum">
                        {row.boxes}
                      </Cell>
                    </tr>
                  ))}
                </tbody>
              </table>
              {/* Хвост списка достаётся кнопкой, а не бесконечной лентой:
                  каталог — 1 771 строка, и отдать их сразу значит привезти на
                  складской телефон почти мегабайт ради хвоста, который откроют
                  единицы. Потолок живёт в адресе, поэтому нажатое «ещё»
                  переживает перезагрузку и уезжает по ссылке сменщику. */}
              {list.shown < list.total ? (
                <div className="border-t border-border p-3 text-center">
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => set("n", String(list.shown + PAGE))}
                    className="h-8 rounded-[var(--radius-ui)] border border-border px-3 text-[13px] hover:bg-bg-hover disabled:opacity-60"
                  >
                    {busy
                      ? "Гружу…"
                      : `Показать ещё ${Math.min(PAGE, list.total - list.shown)}`}
                  </button>
                  <p className="mt-1.5 text-[11.5px] text-fg-muted">
                    показано {list.shown} из {list.total}
                  </p>
                </div>
              ) : null}
            </div>
          )}
        </>
      )}
    </main>
  );
}

/**
 * Города строки: где именно лежат её штуки.
 *
 * Чипами, а не колонкой на город: городов сегодня три, но колонки под них
 * развалили бы строку в тот день, когда склад откроется в четвёртом, — а чипы
 * переживут и десятый. Клик ставит отбор по городу оттуда же, где возник
 * вопрос, — тем же приёмом, что поставщик и категория в соседней ячейке.
 */
function Cities({
  by,
  name,
  onPick,
}: {
  by: { city_id: number; units: number }[];
  name: Map<number, string>;
  onPick: (id: number) => void;
}) {
  if (by.length === 0)
    // Не пустая ячейка: пустота читается как «данных нет», а здесь известно
    // ровно обратное — товар заведён, и на складе его сейчас ноль.
    return <span className="text-[11.5px] text-fg-muted">нет в коробах</span>;
  return (
    <span className="flex flex-wrap gap-1">
      {by.map((c) => (
        <button
          key={c.city_id}
          type="button"
          title={`Только товары города ${name.get(c.city_id) ?? c.city_id}`}
          onClick={() => onPick(c.city_id)}
          className="rounded border border-border px-1.5 py-px text-[11.5px] text-fg-muted hover:border-border-strong hover:text-fg"
        >
          {name.get(c.city_id) ?? c.city_id}{" "}
          <span className="tnum font-medium text-fg">{c.units}</span>
        </button>
      ))}
    </span>
  );
}

/**
 * Чип отбора в строке товара: поставщик или категория.
 *
 * Выбранный помечен — иначе, отобрав по поставщику, человек видел бы у каждой
 * строки одну и ту же метку и не понимал, она про отбор или про строку.
 */
function Tag({
  active,
  title,
  onClick,
  children,
}: {
  active: boolean;
  title: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      className={
        "max-w-[180px] truncate rounded border px-1.5 py-px text-[11px] " +
        (active
          ? "border-accent/60 bg-accent-dim text-accent-text"
          : "border-border text-fg-muted hover:border-border-strong hover:text-fg")
      }
    >
      {children}
    </button>
  );
}

/**
 * Размерный ряд чипами. Больше шести сворачивается в счётчик: у модели с
 * одиннадцатью размерами ряд занял бы половину ширины строки, а прочитать
 * его целиком в списке всё равно не нужно — за этим открывают карточку.
 * Остаток не отрезается, а называется числом и остаётся в подсказке.
 */
function Sizes({ sizes }: { sizes: string[] }) {
  if (sizes.length === 0)
    return <span className="text-[11.5px] text-fg-muted">без размера</span>;
  const shown = sizes.slice(0, 6);
  const rest = sizes.length - shown.length;
  return (
    <span className="flex flex-wrap gap-1">
      {shown.map((size) => (
        <span
          key={size}
          className="rounded border border-border bg-bg-elevated px-1.5 py-px text-[11.5px]"
        >
          {size}
        </span>
      ))}
      {rest > 0 ? (
        <span
          title={sizes.join(", ")}
          className="rounded border border-border-strong px-1.5 py-px text-[11.5px] tnum text-fg-muted"
        >
          +{rest} {sizeWord(rest)}
        </span>
      ) : null}
    </span>
  );
}

/**
 * Ячейка таблицы, которая на узком экране становится строкой «подпись —
 * значение». Подпись приезжает из `data-label` через `::before`, поэтому
 * второго набора разметки под мобильный экран не появляется — расходиться
 * им негде. Приём тот же, что в `PlacementTable`.
 */
function Cell({
  label,
  align,
  children,
}: {
  label: string;
  align?: string;
  children: React.ReactNode;
}) {
  return (
    <td
      data-label={label}
      // Та же подпись — адрес ячейки для разметки: замечание приходит как
      // «товары › строка 14623 › Штук», а не цепочкой tailwind-классов.
      data-annot={label}
      className={
        "flex items-center justify-between gap-3 border-b border-border/60 py-1 last:border-0 " +
        "before:text-[11px] before:uppercase before:tracking-[0.04em] before:text-fg-muted before:content-[attr(data-label)] " +
        "md:table-cell md:border-0 md:px-2.5 md:py-1.5 md:before:content-none " +
        (align ?? "")
      }
    >
      {children}
    </td>
  );
}

/**
 * Плитка сводки. `parts` — разложение главного числа на слагаемые: сумма
 * чипов равна числу над ними, потому что считаны они одним запросом.
 */
function Metric({
  label,
  value,
  unit,
  note,
  accent,
  parts,
}: {
  label: string;
  value: string | number;
  unit: string;
  note: string;
  accent?: boolean;
  parts?: {
    key: number;
    name: string;
    units: number;
    active: boolean;
    onClick: () => void;
  }[];
}) {
  return (
    <div className="rounded-[var(--radius-card)] border border-border bg-bg-subtle p-3">
      <dt className="text-[11.5px] uppercase tracking-[0.04em] text-fg-muted">
        {label}
      </dt>
      <dd
        className={
          "mt-1 text-[24px] font-semibold tabular-nums tracking-[-0.02em] " +
          (accent ? "text-accent-text" : "")
        }
      >
        {value}
        <span className="ml-1 text-[13px] font-normal text-fg-muted">
          {unit}
        </span>
      </dd>
      {parts && parts.length > 0 ? (
        <div data-annot="города" className="mt-1.5 flex flex-wrap gap-1">
          {parts.map((p) => (
            <button
              key={p.key}
              type="button"
              onClick={p.onClick}
              title={`Только товары города ${p.name}`}
              className={
                "rounded border px-1.5 py-px text-[12px] " +
                (p.active
                  ? "border-accent/60 bg-accent-dim text-accent-text"
                  : "border-border text-fg-muted hover:border-border-strong hover:text-fg")
              }
            >
              {p.name}{" "}
              <span className="tnum font-medium text-fg">{p.units}</span>
            </button>
          ))}
        </div>
      ) : null}
      {note ? <p className="mt-1 text-[11.5px] text-fg-muted">{note}</p> : null}
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-[var(--radius-card)] border border-border bg-bg-subtle p-6 text-center text-[13px] text-fg-muted">
      {children}
    </div>
  );
}
